import type { Area } from 'react-easy-crop'

export type ImageCropShape = 'rect' | 'round'

export interface ImageCropOptions {
  /** Relación de aspecto del área de recorte (ancho / alto). Por defecto 1 (cuadrado). */
  aspect?: number
  /** Forma del marco de recorte. Solo afecta a la vista; la imagen exportada es siempre rectangular. */
  shape?: ImageCropShape
  /**
   * Lado mayor máximo de la imagen exportada, en píxeles.
   * Si el área recortada es menor no se reescala hacia arriba.
   */
  outputMaxSize?: number
}

const OUTPUT_MIME = 'image/png'

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('No se pudo cargar la imagen'))
    image.src = src
  })
}

function toPngFileName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '') || 'imagen'
  return `${base}.png`
}

/**
 * Recorta la región `area` (en píxeles de la imagen original) y la devuelve como
 * File PNG, conservando la transparencia. Los SVG se rasterizan al recortar.
 */
export async function cropImageToFile(
  imageSrc: string,
  area: Area,
  fileName: string,
  outputMaxSize?: number
): Promise<File> {
  const image = await loadImage(imageSrc)

  const scale =
    outputMaxSize && Math.max(area.width, area.height) > outputMaxSize
      ? outputMaxSize / Math.max(area.width, area.height)
      : 1

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(area.width * scale)
  canvas.height = Math.round(area.height * scale)

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('El navegador no soporta canvas 2D')

  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    canvas.width,
    canvas.height
  )

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, OUTPUT_MIME)
  )
  if (!blob) throw new Error('No se pudo generar la imagen recortada')

  return new File([blob], toPngFileName(fileName), { type: OUTPUT_MIME })
}
